/**
 * Event Modal Store (Zustand)
 *
 * Manages state for the EventDetailModal component:
 * - Modal open/close state
 * - Current event, calendar, and position in carousel
 * - Participant counts cache
 * - Navigation between events
 * - Overview page support (index -1 = calendar overview)
 *
 * PERFORMANCE OPTIMIZATION:
 * - Calendar data is lazy-loaded using async getCalendarWithDetails()
 * - Adds isLoadingCalendar state to show loading indicator while fetching
 */

import { create } from 'zustand';
import { getCalendarWithDetails, getEventIndex } from '../data/calendars';

export const useEventModalStore = create((set, get) => ({
  // Modal visibility
  isOpen: false,

  // Current event data (null when showing overview)
  currentEvent: null,
  currentCalendar: null,
  // -1 = overview page, 0+ = event index
  currentIndex: -1,

  // All events in the calendar (for carousel navigation)
  allEvents: [],

  // Participant counts cache (eventId -> count)
  participantCounts: {},

  // Loading states
  isLoadingParticipants: false,
  isLoadingCalendar: false,

  /**
   * Open modal to calendar overview (first page) - ASYNC
   * @param {string} calendarId - Calendar ID
   */
  openCalendarOverview: async (calendarId) => {
    set({ isLoadingCalendar: true });

    try {
      const calendar = await getCalendarWithDetails(calendarId);

      if (!calendar) {
        console.error('Calendar not found:', calendarId);
        set({ isLoadingCalendar: false });
        return;
      }

      set({
        isOpen: true,
        currentEvent: null, // null = showing overview
        currentCalendar: calendar,
        currentIndex: -1, // -1 = overview page
        allEvents: calendar.events || [],
        isLoadingCalendar: false,
      });
    } catch (error) {
      console.error('Failed to load calendar:', calendarId, error);
      set({ isLoadingCalendar: false });
    }
  },

  /**
   * Open modal with a specific event - ASYNC
   * @param {string} calendarId - Calendar ID
   * @param {string} eventId - Event ID (or index if no ID)
   * @param {number} eventIndex - Optional event index for basic calendars
   */
  openModal: async (calendarId, eventId, eventIndex = null) => {
    set({ isLoadingCalendar: true });

    try {
      const calendar = await getCalendarWithDetails(calendarId);

      if (!calendar) {
        console.error('Calendar not found:', calendarId);
        set({ isLoadingCalendar: false });
        return;
      }

      // Find the event - try by ID first, then by index
      let event = null;
      let index = -1;

      if (eventId) {
        index = calendar.events.findIndex(e => e.id === eventId);
        if (index !== -1) {
          event = calendar.events[index];
        }
      }

      // Fallback to index if ID didn't match
      if (!event && eventIndex !== null && eventIndex >= 0 && eventIndex < calendar.events.length) {
        index = eventIndex;
        event = calendar.events[index];
      }

      if (!event) {
        console.error('Event not found:', eventId, 'in calendar:', calendarId);
        set({ isLoadingCalendar: false });
        return;
      }

      set({
        isOpen: true,
        currentEvent: event,
        currentCalendar: calendar,
        currentIndex: index,
        allEvents: calendar.events,
        isLoadingCalendar: false,
      });
    } catch (error) {
      console.error('Failed to load calendar:', calendarId, error);
      set({ isLoadingCalendar: false });
    }
  },

  /**
   * Close the modal
   */
  closeModal: () => {
    set({
      isOpen: false,
      // Keep data for smooth close animation
    });

    // Clear data after animation
    setTimeout(() => {
      const { isOpen } = get();
      if (!isOpen) {
        set({
          currentEvent: null,
          currentCalendar: null,
          currentIndex: 0,
          allEvents: [],
        });
      }
    }, 300);
  },

  /**
   * Navigate to next event in carousel
   * From overview (-1) goes to first event (0)
   * From last event wraps to overview (-1)
   */
  nextEvent: () => {
    const { allEvents, currentIndex } = get();

    if (allEvents.length === 0) return;

    // From overview, go to first event
    if (currentIndex === -1) {
      set({
        currentEvent: allEvents[0],
        currentIndex: 0,
      });
      return;
    }

    // From last event, wrap to overview
    if (currentIndex === allEvents.length - 1) {
      set({
        currentEvent: null,
        currentIndex: -1,
      });
      return;
    }

    // Otherwise, go to next event
    const newIndex = currentIndex + 1;
    set({
      currentEvent: allEvents[newIndex],
      currentIndex: newIndex,
    });
  },

  /**
   * Navigate to previous event in carousel
   * From first event (0) goes to overview (-1)
   * From overview wraps to last event
   */
  prevEvent: () => {
    const { allEvents, currentIndex } = get();

    if (allEvents.length === 0) return;

    // From overview, go to last event
    if (currentIndex === -1) {
      const lastIndex = allEvents.length - 1;
      set({
        currentEvent: allEvents[lastIndex],
        currentIndex: lastIndex,
      });
      return;
    }

    // From first event, go to overview
    if (currentIndex === 0) {
      set({
        currentEvent: null,
        currentIndex: -1,
      });
      return;
    }

    // Otherwise, go to previous event
    const newIndex = currentIndex - 1;
    set({
      currentEvent: allEvents[newIndex],
      currentIndex: newIndex,
    });
  },

  /**
   * Navigate to specific event by index
   * @param {number} index - Event index (-1 for overview)
   */
  goToEvent: (index) => {
    const { allEvents } = get();

    // -1 is overview page
    if (index === -1) {
      set({
        currentEvent: null,
        currentIndex: -1,
      });
      return;
    }

    if (index < 0 || index >= allEvents.length) return;

    set({
      currentEvent: allEvents[index],
      currentIndex: index,
    });
  },

  /**
   * Set participant count for an event
   * @param {string} eventId - Event ID
   * @param {number} count - Participant count
   */
  setParticipantCount: (eventId, count) => {
    set(state => ({
      participantCounts: {
        ...state.participantCounts,
        [eventId]: count,
      },
    }));
  },

  /**
   * Increment participant count for an event
   * @param {string} eventId - Event ID
   */
  incrementParticipantCount: (eventId) => {
    set(state => ({
      participantCounts: {
        ...state.participantCounts,
        [eventId]: (state.participantCounts[eventId] || 0) + 1,
      },
    }));
  },

  /**
   * Get participant count for an event
   * @param {string} eventId - Event ID
   * @returns {number} - Participant count (0 if not cached)
   */
  getParticipantCount: (eventId) => {
    return get().participantCounts[eventId] || 0;
  },

  /**
   * Set loading state for participant data
   * @param {boolean} loading - Loading state
   */
  setLoadingParticipants: (loading) => {
    set({ isLoadingParticipants: loading });
  },

  /**
   * Clear all participant counts (e.g., on logout)
   */
  clearParticipantCounts: () => {
    set({ participantCounts: {} });
  },
}));

export default useEventModalStore;
